import {
  useTemplateClientId,
  useTemplateId,
  useTemplateProcessId,
  useTemplateReferenceId,
  useTemplateRevision,
  useTemplateVisibility,
} from 'hooks/useTemplate';
import { useUser } from 'hooks/useUser';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useAppSelector } from 'rtk/query/hooks';
import { ETemplateRevision } from 'rtk/TemplateSlice';
import type { TTemplateBlock, TVisibility } from 'types/template/template.types';
import type { TRecruiterAppUser } from 'types/user.types';
import { isHubertAdminUser } from 'types/user.types';
import { TEMPLATE_PERMISSIONS } from '@hubertai/screening-question-types';

// Define the type for the context state
type TTemplateBuilderContext = {
  visibility?: TVisibility;
  templateId: string;
  templateReferenceId: string;
  processId: string;
  revision: ETemplateRevision;
  isReadOnly: boolean;
  canUpdateTemplate: boolean;
  clientId: string | undefined;

  areNodesCollapsed: boolean;
  toggleAreNodesCollapsed: (force?: boolean) => void;
  areBlocksCollapsed: boolean;
  toggleAreBlocksCollapsed: (force?: boolean) => void;

  showDeletedQuestionsInSummary: boolean;
  toggleShowDeletedQuestionsInSummary: () => void;

  getTemplateBlockById: (blockId: string) => { block: TTemplateBlock | undefined; index: number };

  templateCommentSearchParam?: string;
  questionIdSearchParam?: string;
};

// Create the context with default undefined value
const TemplateBuilderContext = createContext<TTemplateBuilderContext | undefined>(undefined);

// Define the props for the provider component
type TemplateBuilderContextProviderProps = {
  children: ReactNode;
};

// Provider component
export const TemplateBuilderContextProvider = ({ children }: TemplateBuilderContextProviderProps) => {
  const user = useUser();
  const [searchParams, _] = useSearchParams();
  const templateId = useTemplateId();

  const blocks = useAppSelector((state) => state.Template.present.c!.blocks.blocks);
  const templateReferenceId = useTemplateReferenceId();
  const clientId = useTemplateClientId();
  const revision = useTemplateRevision();
  const processId = useTemplateProcessId();
  const visibility = useTemplateVisibility();

  // Collapsed states
  const [areNodesCollapsed, setAreNodesCollapsed] = useState(true);
  const toggleAreNodesCollapsed = (force?: boolean) => {
    if (force !== undefined) {
      setAreNodesCollapsed(force);
      return;
    }
    setAreNodesCollapsed(!areNodesCollapsed);
  };
  const [areBlocksCollapsed, setAreBlocksCollapsed] = useState(false);
  const toggleAreBlocksCollapsed = (force?: boolean) => {
    if (force !== undefined) {
      setAreBlocksCollapsed(force);
      return;
    }
    setAreBlocksCollapsed(!areBlocksCollapsed);
  };

  const [showDeletedQuestionsInSummary, setShowDeletedQuestionsInSummary] = useState(false);
  const toggleShowDeletedQuestionsInSummary = () => {
    setShowDeletedQuestionsInSummary(!showDeletedQuestionsInSummary);
  };

  const hasUpdatePermission = useMemo(() => {
    const canUpdateTemplate = (requireShareOrg = false) => {
      const recruiterUser = user as TRecruiterAppUser;
      return requireShareOrg
        ? recruiterUser.access_groups_permissions?.some(
            (ag) =>
              ag.permissions.includes(TEMPLATE_PERMISSIONS.Update) &&
              ag.permissions.includes(TEMPLATE_PERMISSIONS.ShareOrg),
          ) ||
            recruiterUser.access_groups_permissions?.some((ag) =>
              ag.permissions.includes(TEMPLATE_PERMISSIONS.ManageClientLevel),
            )
        : recruiterUser.access_groups_permissions?.some(
            (ag) =>
              ag.permissions.includes(TEMPLATE_PERMISSIONS.Update) &&
              ag.resources.some(
                (r) => (r.resourceId === templateReferenceId || r.resourceId === templateId) && r.type === 'template',
              ),
          ) ||
            recruiterUser.access_groups_permissions?.some((ag) =>
              ag.permissions.includes(TEMPLATE_PERMISSIONS.ManageClientLevel),
            );
    };

    if (isHubertAdminUser(user)) return true;
    if (visibility === 'global') return false;
    if (visibility === 'client') return canUpdateTemplate(true) ?? false;
    return canUpdateTemplate() ?? false;
  }, [user, visibility, templateReferenceId, templateId]);

  const isReadOnly = useMemo(() => {
    if (revision === ETemplateRevision.Current) return true;
    return !hasUpdatePermission;
  }, [revision, hasUpdatePermission]);

  const [templateCommentSearchParam, setTemplateCommentSearchParam] = useState<string>();
  const [questionIdSearchParam, setQuestionIdSearchParam] = useState<string>();

  const getTemplateBlockById = useCallback(
    (blockId: string): { block: TTemplateBlock | undefined; index: number } => {
      const noFind = { block: undefined, index: -1 };
      if (!blocks) return noFind;
      const index = blocks.findIndex((x) => x.id === blockId);
      if (index > -1 && blocks[index]) {
        return { block: blocks[index], index };
      }
      return noFind;
    },
    [blocks],
  );
  useEffect(() => {
    const commentId = searchParams.get('commentId');
    if (commentId && commentId.length) {
      setTemplateCommentSearchParam(commentId);
    }
    const questionId = searchParams.get('id');
    setQuestionIdSearchParam(questionId || undefined);
  }, [searchParams]);

  const contextValue = {
    visibility,
    templateId,
    templateReferenceId,
    revision,
    processId,
    isReadOnly,
    canUpdateTemplate: hasUpdatePermission,
    clientId,
    // Collapsible states
    areNodesCollapsed,
    toggleAreNodesCollapsed,
    areBlocksCollapsed,
    toggleAreBlocksCollapsed,

    getTemplateBlockById,

    showDeletedQuestionsInSummary,
    toggleShowDeletedQuestionsInSummary,

    templateCommentSearchParam,
    questionIdSearchParam,
  };

  return <TemplateBuilderContext.Provider value={contextValue}>{children}</TemplateBuilderContext.Provider>;
};

export const useTemplateBuilderContext = (): TTemplateBuilderContext => {
  const context = useContext(TemplateBuilderContext);
  if (!context) {
    throw new Error('useTemplateBuilderContext must be used within a TemplateBuilderContext');
  }
  return context;
};
